import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { User } from 'lucide-react';
import CardHeader from '../components/public-card/CardHeader';
import ContactForm from '../components/public-card/ContactForm';
import LinksContent from '../components/public-card/LinksContent';
import BookingSection from '../components/public-card/BookingSection';
import CardFooter from '../components/public-card/CardFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicCard = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', notes: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [savedContact, setSavedContact] = useState(false);

  const trackLinkClick = (linkName, linkUrl) => {
    axios.post(`${API}/p/${username}/link-click`, { link_name: linkName, link_url: linkUrl }).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/p/${username}`).then(res => setProfile(res.data)).catch(() => setNotFound(true));
    axios.post(`${API}/p/${username}/view`).catch(() => {});
  }, [username]);

  const customFont = profile?.theme?.custom_font;
  useEffect(() => {
    if (customFont) {
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${customFont.replace(/ /g, '+')}&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      return () => document.head.removeChild(link);
    }
  }, [customFont]);

  if (notFound) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-heading text-6xl font-light text-gray-900 mb-3">404</h1>
        <p className="text-gray-500 text-sm">This card doesn't exist yet.</p>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin"></div>
    </div>
  );

  const t = profile.theme || {};
  const isDark = (t.bg_color || '#09090b') !== '#ffffff' && (t.bg_color || '#09090b') !== '#f2fce2' && (t.bg_color || '#09090b') !== '#fffbeb';
  const themeVars = {
    isDark,
    subtleBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    subtleBg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    mutedText: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const cardUrl = `${siteUrl}/u/${username}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); setError('');
    let lat = null, lng = null;
    try { const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })); lat = pos.coords.latitude; lng = pos.coords.longitude; } catch {}
    try {
      await axios.post(`${API}/p/${username}/contact`, { ...formData, phone: countryCode + formData.phone.replace(/\D/g, ''), lat, lng });
      setStep('links');
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  const downloadVCard = async () => {
    try {
      const res = await axios.get(`${API}/p/${username}/vcard`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${profile.profile?.name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      setSavedContact(true);
    } catch {}
  };

  const fontStyle = customFont ? { fontFamily: `'${customFont}', sans-serif` } : {};

  const textureClass = t.card_texture && t.card_texture !== 'none' ? `card-texture-${t.card_texture}` : '';
  const effectClass = t.card_effect && t.card_effect !== 'none' ? `card-effect-${t.card_effect}` : '';

  const meshVars = {
    '--mesh-color-1': `${t.accent_color || '#6366f1'}66`,
    '--mesh-color-2': `${t.primary_color || '#a855f7'}44`,
    '--glow-color': `${t.accent_color || '#6366f1'}55`,
    '--glow-color-2': `${t.primary_color || '#a855f7'}44`,
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center px-4 py-12 ${textureClass} ${effectClass}`}
      style={{ background: t.bg_color || '#09090b', color: t.text_color || '#fafafa', ...fontStyle, ...meshVars }}
      data-testid="public-card-page"
    >
      <div className="w-full max-w-md mx-auto relative z-10 card-entrance">
        <CardHeader profile={profile} theme={t} themeVars={themeVars} />

        {step === 'form' ? (
          <ContactForm
            formData={formData} setFormData={setFormData}
            countryCode={countryCode} setCountryCode={setCountryCode}
            onSubmit={handleSubmit} onSkip={() => setStep('links')}
            isSubmitting={isSubmitting} error={error}
            theme={t} themeVars={themeVars}
          />
        ) : (
          <LinksContent
            profile={profile} theme={t} themeVars={themeVars}
            onLinkClick={trackLinkClick} savedContact={savedContact}
            onDownloadVCard={downloadVCard}
          />
        )}

        {/* Custom Fields */}
        {profile.custom_fields && profile.custom_fields.length > 0 && (
          <div className="mt-8" data-testid="public-custom-fields">
            <p className="text-xs font-semibold uppercase tracking-wider mb-4 text-center" style={{ color: themeVars.mutedText }}>Details</p>
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${themeVars.subtleBorder}` }}>
              {profile.custom_fields.map((field, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3"
                  style={{ background: themeVars.subtleBg, borderBottom: idx < profile.custom_fields.length - 1 ? `1px solid ${themeVars.subtleBorder}` : 'none' }}
                  data-testid={`public-field-${idx}`}>
                  <span className="text-xs font-semibold" style={{ color: themeVars.mutedText }}>{field.label}</span>
                  <span className="text-sm font-medium" style={{ color: t.text_color }}>{field.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <BookingSection username={username} profile={profile} theme={t} themeVars={themeVars} />
        <CardFooter cardUrl={cardUrl} theme={t} themeVars={themeVars} />
      </div>
    </div>
  );
};

export default PublicCard;
